// /reports/linked-accounts — one row per linked-account record (mig 0014),
// for Excel pivots. Filters: status + the FRT account's customer. Preview
// shows the first 50 rows; Export pulls everything.

import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { customers, reports } from '@/lib/api';

type StatusFilter = '' | 'linked' | 'previously_linked';

export function LinkedAccountsExportPage() {
  const [status, setStatus] = useState<StatusFilter>('');
  const [customerId, setCustomerId] = useState<number | ''>('');

  const customerList = useQuery({
    queryKey: ['customers', { per_page: 200, is_customer: true }],
    queryFn: () => customers.list({ per_page: 200, is_customer: true }),
  });

  const query = {
    ...(status ? { status } : {}),
    ...(customerId !== '' ? { frt_customer_id: customerId } : {}),
  };
  const preview = useQuery({
    queryKey: ['linked-accounts-export', query],
    queryFn: () => reports.linkedAccounts(query),
  });

  const onExport = async (format: 'xlsx' | 'csv') => {
    try {
      await reports.linkedAccountsDownload(query, format);
    } catch (err) {
      console.error(err);
    }
  };

  const rows = preview.data?.rows ?? [];
  const previewRows = rows.slice(0, 50);

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-semibold text-slate-900">Linked Accounts Export</h1>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button size="sm" variant="secondary" onClick={() => onExport('xlsx')}>
            Export XLSX
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onExport('csv')}>
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
          <label className="block">
            <span className="block text-slate-600">Status</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
            >
              <option value="">All</option>
              <option value="linked">Linked</option>
              <option value="previously_linked">Previously linked</option>
            </select>
          </label>
          <label className="block">
            <span className="block text-slate-600">FRT account customer</span>
            <select
              className="w-full rounded border border-slate-300 bg-white px-2 py-1.5"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">All</option>
              {(customerList.data?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code}
                </option>
              ))}
            </select>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            {preview.isLoading
              ? 'Loading…'
              : `${rows.length} linked-account record${rows.length === 1 ? '' : 's'}`}
          </CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto">
          {preview.error && (
            <div className="text-sm text-red-600 mb-2">{(preview.error as Error).message}</div>
          )}
          {!preview.isLoading && rows.length === 0 && (
            <div className="text-sm text-slate-500">No linked accounts match these filters.</div>
          )}
          {previewRows.length > 0 && (
            <>
              <p className="text-xs text-slate-500 mb-2">
                Preview (first 50 rows). The Export buttons pull all {rows.length} rows.
              </p>
              <table className="w-full text-xs">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-2">Account</th>
                    <th className="py-1 pr-2">Linked account</th>
                    <th className="py-1 pr-2">Bank</th>
                    <th className="py-1 pr-2">Product</th>
                    <th className="py-1 pr-2">Last 4</th>
                    <th className="py-1 pr-2">Status</th>
                    <th className="py-1 pr-2">Linked</th>
                    <th className="py-1 pr-2">Unlinked</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((r) => (
                    <tr key={r.link_id} className="border-t border-slate-100">
                      <td className="py-1 pr-2 font-mono">
                        <Link
                          to="/accounts/$id"
                          params={{ id: String(r.account_id) }}
                          className="text-brand-700 hover:underline"
                        >
                          {r.account_display_name}
                        </Link>
                      </td>
                      <td className="py-1 pr-2 font-mono">
                        {r.linked_account_display_name ?? 'Non-FRT account'}
                      </td>
                      <td className="py-1 pr-2">
                        {r.linked_bank_code}
                        {!r.linked_bank_is_customer && (
                          <span className="ml-1 text-slate-400">(non-customer)</span>
                        )}
                      </td>
                      <td className="py-1 pr-2">{r.linked_product_type}</td>
                      <td className="py-1 pr-2 font-mono">{r.linked_last4}</td>
                      <td className="py-1 pr-2">
                        {r.status === 'linked' ? 'Linked' : 'Previously linked'}
                      </td>
                      <td className="py-1 pr-2 font-mono">{r.linked_on}</td>
                      <td className="py-1 pr-2 font-mono">{r.unlinked_on ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
